import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  Divider
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

export default function QuestionForm({
  question = null,
  onSave,
  onCancel,
  loading = false,
  error = null
}) {
  const [text, setText] = useState(question?.text || "");
  const [answerChoices, setAnswerChoices] = useState(
    question?.answerChoices?.length
      ? (() => {
        const choices = question.answerChoices.map(choice => ({
          id: choice.id,
          text: choice.text,
          points: choice.points.toString()
        }));
        // Ensure "Not sure" is first
        const notSureIndex = choices.findIndex(choice =>
          choice.text.trim().toLowerCase() === "not sure"
        );
        if (notSureIndex > 0) {
          const notSure = choices.splice(notSureIndex, 1)[0];
          choices.unshift(notSure);
        }
        return choices;
      })()
      : [
        { text: "Not sure", points: "0" },
        { text: "", points: "" },
        { text: "", points: "" }
      ]
  );
  const [validationError, setValidationError] = useState("");

  const isEditing = Boolean(question);

  const addAnswerChoice = () => {
    setAnswerChoices([...answerChoices, { text: "", points: "" }]);
  };

  const removeAnswerChoice = (index) => {
    if (answerChoices.length > 2) {
      // Don't allow removing the "Not sure" answer
      const choiceToRemove = answerChoices[index];
      if (choiceToRemove.text.trim().toLowerCase() === "not sure") {
        setValidationError("The 'Not sure' answer choice cannot be removed as it's required for automatic rating generation");
        return;
      }
      setAnswerChoices(answerChoices.filter((_, i) => i !== index));
    }
  };

  const updateAnswerChoice = (index, field, value) => {
    const updated = [...answerChoices];

    // Prevent changing "Not sure" text
    if (field === "text" && updated[index].text.trim().toLowerCase() === "not sure") {
      return;
    }

    updated[index][field] = value;
    setAnswerChoices(updated);

    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError("");

    if (!text.trim()) {
      setValidationError("Question text is required");
      return;
    }

    const validChoices = answerChoices.filter(choice => choice.text.trim());

    if (validChoices.length < 2) {
      setValidationError("Questions must have at least 2 answer choices");
      return;
    }

    // Check for required "Not sure" answer
    const hasNotSure = validChoices.some(choice =>
      choice.text.trim().toLowerCase() === "not sure"
    );
    if (!hasNotSure) {
      setValidationError("Questions must include a 'Not sure' answer choice for automatic rating generation");
      return;
    }

    for (const choice of validChoices) {
      if (isNaN(Number(choice.points))) {
        setValidationError("Points must be a valid number");
        return;
      }
    }

    onSave({
      text: text.trim(),
      answerChoices: validChoices.map(choice => ({
        id: choice.id,
        text: choice.text.trim(),
        points: Number(choice.points)
      }))
    });
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 3,
        mb: 2,
        border: 1,
        borderColor: "grey.300"
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {isEditing ? "Edit Question" : "Add Question"}
      </Typography>

      <TextField
        label="Question Text"
        fullWidth
        multiline
        rows={2}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (validationError) {
            setValidationError("");
          }
        }}
        disabled={loading}
        error={!!validationError || !!error}
        helperText={validationError || error?.message}
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Add Answer Choices
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Note: All questions must include a &quot;Not sure&quot; answer choice. This is required for automatic rating generation.
      </Typography>

      {answerChoices.map((choice, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              label={`Answer Text ${index + 1}`}
              value={choice.text}
              onChange={(e) => updateAnswerChoice(index, "text", e.target.value)}
              disabled={loading}
              sx={{
                flex: 2,
                ...(choice.text.trim().toLowerCase() === "not sure" && {
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)"
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    borderWidth: "1px"
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "rgba(0, 0, 0, 0.6)"
                  },
                  "& .MuiOutlinedInput-input": {
                    cursor: "default",
                    userSelect: "none"
                  }
                })
              }}
              size="small"
              InputProps={{
                readOnly: choice.text.trim().toLowerCase() === "not sure"
              }}
            />
            <TextField
              label="Points"
              type="number"
              value={choice.points}
              onChange={(e) => updateAnswerChoice(index, "points", e.target.value)}
              disabled={loading}
              sx={{ flex: 1 }}
              size="small"
            />
            {answerChoices.length > 2 && choice.text.trim().toLowerCase() !== "not sure" && (
              <IconButton
                onClick={() => removeAnswerChoice(index)}
                disabled={loading}
                aria-label={`Remove answer choice ${index + 1}`}
                size="small"
              >
                <RemoveIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={addAnswerChoice}
        disabled={loading}
        sx={{ mb: 3 }}
      >
        Add Answer Choice
      </Button>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
        >
          {loading ? "Saving..." : isEditing ? "Save" : "Save Question"}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </Box>
    </Paper>
  );
}
